/**
 * Shared 3D room builder from AI-generated configs
 * Used by both MetaverseViewerPage and DigitalTwinGeneratorPage
 */
import * as THREE from 'three';

export function buildRoomFromAIConfig(scene, cfg) {
  while (scene.children.length > 0) scene.remove(scene.children[0]);

  const dim = cfg.estimated_dimensions || { width_meters: 6, height_meters: 3.2, depth_meters: 5 };
  const w = dim.width_meters;
  const h = dim.height_meters;
  const d = dim.depth_meters;
  const colors = cfg.colors || {};
  const wallColor = colors.wall || '#e8e0d4';
  const floorColor = colors.floor || '#8B7355';
  const ceilingColor = colors.ceiling || '#f5f0e8';
  const accentColor = colors.accent || '#F7931A';

  // Lighting
  const lightCfg = cfg.lighting || {};
  scene.add(new THREE.AmbientLight(0xffffff, lightCfg.ambient_intensity || 0.45));
  const dir = new THREE.DirectionalLight(0xffffff, lightCfg.main_light_intensity || 0.6);
  dir.position.set(5, 8, 3);
  dir.castShadow = true;
  scene.add(dir);

  // Floor
  const floorMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshStandardMaterial({ color: floorColor, roughness: 0.6 })
  );
  floorMesh.rotation.x = -Math.PI / 2;
  floorMesh.receiveShadow = true;
  scene.add(floorMesh);

  // Ceiling
  const ceil = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshStandardMaterial({ color: ceilingColor, roughness: 0.9 })
  );
  ceil.rotation.x = Math.PI / 2;
  ceil.position.y = h;
  scene.add(ceil);

  // Walls
  const wallMat = new THREE.MeshStandardMaterial({ color: wallColor, roughness: 0.8 });
  const backW = new THREE.Mesh(new THREE.PlaneGeometry(w, h), wallMat);
  backW.position.set(0, h / 2, -d / 2);
  scene.add(backW);
  const leftW = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
  leftW.position.set(-w / 2, h / 2, 0);
  leftW.rotation.y = Math.PI / 2;
  scene.add(leftW);
  const rightW = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
  rightW.position.set(w / 2, h / 2, 0);
  rightW.rotation.y = -Math.PI / 2;
  scene.add(rightW);

  // Accent strip
  const accentMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.6, h * 0.4),
    new THREE.MeshStandardMaterial({ color: accentColor, roughness: 0.5, transparent: true, opacity: 0.18 })
  );
  accentMesh.position.set(0, h * 0.55, -d / 2 + 0.01);
  scene.add(accentMesh);

  // Window
  const winCfg = cfg.window || {};
  const winGroup = new THREE.Group();
  const winPos = winCfg.position || 'right_wall';
  if (winPos.includes('right')) {
    winGroup.position.set(w / 2 - 0.01, h * 0.55, -d * 0.15);
    winGroup.rotation.y = -Math.PI / 2;
  } else {
    winGroup.position.set(-w / 2 + 0.01, h * 0.55, -d * 0.15);
    winGroup.rotation.y = Math.PI / 2;
  }
  const winSize = winCfg.size === 'large' ? 2.6 : winCfg.size === 'small' ? 1.4 : 2.2;
  winGroup.add(new THREE.Mesh(
    new THREE.PlaneGeometry(winSize, winSize * 0.8),
    new THREE.MeshStandardMaterial({ color: 0x87ceeb, emissive: 0x87ceeb, emissiveIntensity: 0.3, roughness: 0.1 })
  ));
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b7355 });
  [winSize * 0.4, -winSize * 0.4].forEach(y => {
    const fh = new THREE.Mesh(new THREE.BoxGeometry(winSize + 0.1, 0.06, 0.03), frameMat);
    fh.position.set(0, y, 0.01);
    winGroup.add(fh);
  });
  const winLight = new THREE.PointLight(0xfff5e1, 0.8, 6);
  winLight.position.set(0, 0, 0.5);
  winGroup.add(winLight);
  scene.add(winGroup);

  // Bed
  const bed = cfg.bed || {};
  const bw = bed.width_meters || 1.8;
  const darkWood = new THREE.MeshStandardMaterial({ color: bed.headboard_color || '#2d1f14', roughness: 0.6 });
  const bedGroup = new THREE.Group();
  bedGroup.position.set(0, 0, -d / 2 + 1.5);

  const frame = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.5, 2.4), new THREE.MeshStandardMaterial({ color: '#3d2b1f', roughness: 0.7 }));
  frame.position.y = 0.25;
  frame.castShadow = true;
  bedGroup.add(frame);

  const mattress = new THREE.Mesh(new THREE.BoxGeometry(bw - 0.1, 0.25, 2.3), new THREE.MeshStandardMaterial({ color: bed.color || '#f5f5f0', roughness: 0.9 }));
  mattress.position.y = 0.62;
  bedGroup.add(mattress);

  const duvet = new THREE.Mesh(new THREE.BoxGeometry(bw - 0.15, 0.08, 1.4), new THREE.MeshStandardMaterial({ color: accentColor, roughness: 0.8, transparent: true, opacity: 0.9 }));
  duvet.position.set(0, 0.78, 0.15);
  bedGroup.add(duvet);

  [-0.4, 0.4].forEach(x => {
    const p = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.4), new THREE.MeshStandardMaterial({ color: '#fffff0', roughness: 0.95 }));
    p.position.set(x, 0.82, -0.8);
    bedGroup.add(p);
  });

  const headboard = new THREE.Mesh(new THREE.BoxGeometry(bw + 0.2, 1.2, 0.08), darkWood);
  headboard.position.set(0, 1.1, -1.16);
  bedGroup.add(headboard);

  // Nightstands + lamps
  if (lightCfg.bedside_lamps !== false) {
    [-bw / 2 - 0.45, bw / 2 + 0.45].forEach(x => {
      const ns = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.55, 0.45), new THREE.MeshStandardMaterial({ color: '#3d2b1f', roughness: 0.7 }));
      ns.position.set(x, 0.275, -0.9);
      ns.castShadow = true;
      bedGroup.add(ns);
      const lampBase = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.3, 0.12), new THREE.MeshStandardMaterial({ color: accentColor, roughness: 0.4, metalness: 0.3 }));
      lampBase.position.set(x, 0.7, -0.9);
      bedGroup.add(lampBase);
      const lampShade = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.04, 0.28), new THREE.MeshStandardMaterial({ color: lightCfg.lamp_color || '#fff8e7', roughness: 0.9 }));
      lampShade.position.set(x, 0.9, -0.9);
      bedGroup.add(lampShade);
      const lp = new THREE.PointLight(lightCfg.lamp_color || 0xffe4b5, 0.3, 2);
      lp.position.set(x, 0.85, -0.9);
      bedGroup.add(lp);
    });
  }
  scene.add(bedGroup);

  // Dynamic furniture
  const posMap = {
    right_front: [w / 2 - 0.8, 0, d / 2 - 1],
    left_front: [-w / 2 + 0.8, 0, d / 2 - 0.6],
    left_wall: [-w / 2 + 0.06, 1.3, -d * 0.15],
    right_wall: [w / 2 - 0.06, 1.3, -d * 0.15],
    left_back: [-w / 2 + 0.02, 0, -d / 2 + 1.2],
    right_back: [w / 2 - 0.8, 0, -d / 2 + 1.2],
    right_center: [w / 4, 0, d / 4],
    center: [0, 0, d / 4],
    corner: [-w / 2 + 1, 0, d / 2 - 1],
  };

  (cfg.furniture || []).forEach(f => {
    const pos = posMap[f.position] || [0, 0, 0];
    const matColor = f.material_color || '#5c4033';

    if (f.type === 'desk') {
      const g = new THREE.Group();
      g.position.set(...pos);
      const top = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.04, 0.6), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.5 }));
      top.position.y = 0.75;
      top.castShadow = true;
      g.add(top);
      [[-0.55, -0.25], [0.55, -0.25], [-0.55, 0.25], [0.55, 0.25]].forEach(([x, z]) => {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.75, 0.04), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.7 }));
        leg.position.set(x, 0.375, z);
        g.add(leg);
      });
      const chairSeat = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.04, 0.45), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.6 }));
      chairSeat.position.set(0, 0.45, 0.55);
      g.add(chairSeat);
      const chairBack = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.5, 0.04), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.6 }));
      chairBack.position.set(0, 0.7, 0.76);
      g.add(chairBack);
      scene.add(g);
    }

    if (f.type === 'wardrobe') {
      const wb = new THREE.Mesh(new THREE.BoxGeometry(0.6, 2.2, 0.5), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.7 }));
      wb.position.set(pos[0], 1.1, pos[2]);
      wb.castShadow = true;
      scene.add(wb);
    }

    if (f.type === 'tv') {
      const tvG = new THREE.Group();
      tvG.position.set(...pos);
      tvG.rotation.y = pos[0] < 0 ? Math.PI / 2 : -Math.PI / 2;
      const tvW = f.size === 'large' ? 1.5 : f.size === 'small' ? 0.8 : 1.2;
      tvG.add(new THREE.Mesh(new THREE.BoxGeometry(tvW, tvW * 0.58, 0.04), new THREE.MeshStandardMaterial({ color: '#111', roughness: 0.3 })));
      const scr = new THREE.Mesh(new THREE.PlaneGeometry(tvW - 0.05, tvW * 0.54), new THREE.MeshStandardMaterial({ color: '#1a1a2e', emissive: '#1a1a2e', emissiveIntensity: 0.05 }));
      scr.position.z = 0.025;
      tvG.add(scr);
      scene.add(tvG);
    }

    if (f.type === 'sofa') {
      const sg = new THREE.Group();
      sg.position.set(...pos);
      const seat = new THREE.Mesh(new THREE.BoxGeometry(2, 0.45, 0.8), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.8 }));
      seat.position.y = 0.225;
      seat.castShadow = true;
      sg.add(seat);
      const back = new THREE.Mesh(new THREE.BoxGeometry(2, 0.4, 0.12), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.8 }));
      back.position.set(0, 0.55, -0.34);
      sg.add(back);
      const cushion = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.35, 0.1), new THREE.MeshStandardMaterial({ color: accentColor, transparent: true, opacity: 0.8 }));
      cushion.position.set(-0.6, 0.6, -0.25);
      sg.add(cushion);
      const c2 = cushion.clone();
      c2.position.x = 0.6;
      sg.add(c2);
      scene.add(sg);
    }

    if (f.type === 'coffee_table') {
      const ct = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.03, 0.5), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.4 }));
      ct.position.set(pos[0], 0.4, pos[2] + 0.7);
      scene.add(ct);
    }

    if (f.type === 'armchair') {
      const ag = new THREE.Group();
      ag.position.set(...pos);
      const armSeat = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.35, 0.6), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.8 }));
      armSeat.position.y = 0.175;
      ag.add(armSeat);
      const armBack = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 0.08), new THREE.MeshStandardMaterial({ color: matColor, roughness: 0.8 }));
      armBack.position.set(0, 0.5, -0.26);
      ag.add(armBack);
      scene.add(ag);
    }
  });

  // Bathroom door
  const bathDoor = cfg.bathroom_door || {};
  const doorPosKey = bathDoor.position || 'left_back';
  const doorPos = posMap[doorPosKey] || [-w / 2 + 0.02, 0, -d / 2 + 1.2];
  const doorG = new THREE.Group();
  doorG.position.set(doorPos[0], doorPos[1], doorPos[2]);
  doorG.rotation.y = doorPos[0] < 0 ? Math.PI / 2 : -Math.PI / 2;
  const doorMesh = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.2, 0.08), new THREE.MeshStandardMaterial({ color: bathDoor.color || '#f5f0e8', roughness: 0.7 }));
  doorMesh.position.y = 1.1;
  doorG.add(doorMesh);
  const handleMesh = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.1), new THREE.MeshStandardMaterial({ color: '#c0c0c0', metalness: 0.8, roughness: 0.2 }));
  handleMesh.position.set(0.3, 1.0, 0.05);
  doorG.add(handleMesh);
  scene.add(doorG);
}
